import * as R from "ramda";
import { format } from "date-fns";
import { getCurveName, getGranName } from "../production";

const getDailyDates = R.pipe<any, any, any>(
  R.map((row: any) => format(new Date(row.dateTimeOffset), "dd-MM-yyyy")),
  R.uniq
);
const getHourlyDates = (rows: any[]) => {
  const hourly = getHourlyInfo(rows);
  const seen = new Set<string>();
  const dates: string[] = [];
  for (const r of hourly) {
    if (!seen.has(r.Date)) {
      seen.add(r.Date);
      dates.push(r.Date);
    }
  }
  return dates;
};

const getDailyInfo = R.pipe<any, any, any, any, any>(
  R.map((row: any) =>
    R.assoc("Date", format(new Date(row.dateTimeOffset), "dd-MM-yyyy"), row)
  ),
  R.groupBy((x: any) => `${x.Date}-${x.curveName}`),
  R.map((x: any) => {
    const val = x.reduce(
      (a: any, b: any) => ({
        ...b,
        value: a.value + R.pathOr(0, ["value"], b),
      }),
      {
        value: 0,
      }
    );
    return {
      ...val,
      value: val.value,
    };
  }),
  R.values
);
const getHourlyInfo = (rows: any[]) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  // Gruppo per (curveName + ora)
  const groups: Record<string, any[]> = {};
  for (const row of rows) {
    const d = new Date(row.dateTimeOffset);
    const hourStart = new Date(d);
    hourStart.setMinutes(0, 0, 0);
    const hourKey = `${row.curveName}__${hourStart.getTime()}`;
    (groups[hourKey] = groups[hourKey] || []).push(row);
  }

  const result: any[] = [];
  for (const [key, group] of Object.entries(groups)) {
    const [curveName, tsStr] = key.split("__");
    const hourTs = Number(tsStr);
    // Rileva se il gruppo contiene quarti d'ora (minuti != 0)
    const hasQuarterly = group.some(
      (r) => new Date(r.dateTimeOffset).getMinutes() !== 0
    );

    let value: number | null = null;
    if (hasQuarterly) {
      // Somma tutti i valori (solo quelli numerici)
      value = group.reduce(
        (s, r) => s + (typeof r.value === "number" ? r.value : 0),
        0
      );
    } else {
      // Usa il primo valore orario (o null)
      const first = group.find(
        (r) => typeof r.value === "number" && !isNaN(r.value)
      );
      value = first ? first.value : null;
    }

    result.push({
      curveName,
      Date: new Date(hourTs).toISOString(),
      value,
    });
  }

  // Ordina per timestamp
  result.sort(
    (a, b) =>
      new Date(a.Date).getTime() - new Date(b.Date).getTime() ||
      a.curveName.localeCompare(b.curveName)
  );
  return result;
};

const expandHourlyRowToQuarters = (row: any) => {
  const dt = new Date(row.dateTimeOffset);
  if (dt.getMinutes() !== 0) return [row]; // già quarti
  const baseValue = typeof row.value === "number" ? row.value : 0;
  return [0, 15, 30, 45].map((m) => {
    const q = new Date(dt);
    q.setMinutes(m, 0, 0);
    return {
      ...row,
      dateTimeOffset: q.toISOString(),
      value: baseValue / 4,
    };
  });
};

const getQuarterlyInfo = (
  rows: any[],
  tideDate?: Date | string | number
): any[] => {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const sorted = [...rows].sort(
    (a, b) =>
      new Date(a.dateTimeOffset).getTime() -
      new Date(b.dateTimeOffset).getTime()
  );
  const firstTs = new Date(sorted[0].dateTimeOffset).getTime();
  const lastTs = new Date(sorted[sorted.length - 1].dateTimeOffset).getTime();
  const tideTs =
    tideDate != null ? new Date(tideDate as any).getTime() : undefined;

  let expanded: any[] = [];

  if (tideTs != null && tideTs > firstTs && tideTs < lastTs) {
    // Espansione condizionata a tideDate interno
    for (const row of sorted) {
      const rowTs = new Date(row.dateTimeOffset).getTime();
      if (rowTs < tideTs && new Date(row.dateTimeOffset).getMinutes() === 0) {
        expanded.push(...expandHourlyRowToQuarters(row));
      } else {
        expanded.push(row);
      }
    }
  } else if (tideTs != null && firstTs > tideTs) {
    // Tutto dopo tide -> nessuna espansione
    expanded = sorted;
  } else {
    // Fallback senza tideDate: se dataset misto (alcuni quarti) espandi gli orari puri precedenti al primo quarto
    const hasQuarterly = sorted.some((r) =>
      [15, 30, 45].includes(new Date(r.dateTimeOffset).getMinutes())
    );
    if (tideTs == null && hasQuarterly) {
      // Trova primo quarter timestamp
      const firstQuarterRow = sorted.find((r) =>
        [15, 30, 45].includes(new Date(r.dateTimeOffset).getMinutes())
      );
      const firstQuarterTs = firstQuarterRow
        ? new Date(firstQuarterRow.dateTimeOffset).getTime()
        : undefined;
      for (const row of sorted) {
        const rowTs = new Date(row.dateTimeOffset).getTime();
        if (
          firstQuarterTs != null &&
          rowTs < firstQuarterTs &&
          new Date(row.dateTimeOffset).getMinutes() === 0
        ) {
          expanded.push(...expandHourlyRowToQuarters(row));
        } else {
          expanded.push(row);
        }
      }
    } else {
      expanded = sorted;
    }
  }

  // Aggiunge campo Date (per coerenza con daily/hourly)
  return expanded.map((r) => ({
    ...r,
    Date: r.dateTimeOffset,
  }));
};

// Quarterly dates (dopo eventuale espansione)
const getQuarterlyDates = (rows: any[], tideDate?: Date | string | number) => {
  const info = getQuarterlyInfo(rows, tideDate);
  const seen = new Set<string>();
  const dates: string[] = [];
  for (const r of info) {
    if (!seen.has(r.Date)) {
      seen.add(r.Date);
      dates.push(r.Date);
    }
  }
  return dates;
};

const style = {
  alignment: {
    horizontal: ["center"],
  },
  font: {
    bold: true,
  },
  fill: {
    type: "pattern",
    patternType: "solid",
    bgColor: "#99CC00",
    fgColor: "#99CC00",
  },
};

const getTimeseriesData = (data: any, curves: any) =>
  R.ifElse(
    (x) => R.isEmpty(x) || R.isNil(x),
    () => [],
    R.pipe<any, any, any, any, any, any>(
      R.toPairs,
      R.map(([curveName, val]) => {
        return {
          curveName,
          data: R.map((row) => ({ ...row, curveName }), val.timeseries),
        };
      }),
      R.reject((row: any) => !R.contains(row.curveName, curves || [])),
      R.map(R.prop("data")),
      R.flatten
    )
  )(data);

export const timeseriesHeaders = ({
  curves,
  granularity,
  sheet,
}: {
  curves: any;
  granularity: any;
  sheet: any;
}) => {
  sheet.row(1).freeze();
  sheet.row(2).freeze();
  sheet
    .cell(1, 1)
    .string("Granularity:")
    .style(style);
  sheet
    .cell(1, 2)
    .string(getGranName(granularity))
    .style(style);

  sheet
    .cell(2, 1)
    .string("Date")
    .style(style);
  return curves.map((title: any, i: number) =>
    sheet
      .cell(2, R.add(i, 2))
      .string(getCurveName(title))
      .style(style)
  );
};

const getDates = (data: any) =>
  data.reduce(
    (acc: any, cv: any) =>
      cv.timeseries.length > acc.length ? cv.timeseries : acc,
    []
  );

type BodyType = {
  curves: any;
  sheet: any;
  data: any;
  gran: any;
  tideDate: Date;
};
export const timeseriesBody = ({
  curves,
  sheet,
  data,
  gran,
  tideDate,
}: BodyType) => {
  const maxDatesList = getDates([
    data.forecast,
    data.production,
    data.realizedForecast,
    data.unbalance,
  ]);
  const timeseriesData = getTimeseriesData(data, curves);
  const allDates =
    gran === "daily"
      ? getDailyDates(maxDatesList)
      : gran === "hourly"
      ? getHourlyDates(timeseriesData)
      : getQuarterlyDates(timeseriesData, tideDate);
  const uniformData =
    gran === "daily"
      ? getDailyInfo(timeseriesData)
      : gran === "hourly"
      ? getHourlyInfo(timeseriesData)
      : getQuarterlyInfo(timeseriesData, tideDate);
  return R.pipe(
    R.map((rd) => R.filter(R.propEq("Date", rd), uniformData)),
    R.toPairs,
    (x) => {
      return x.map(([row, arr]: any, ri: number) => {
        const dateVal = R.equals(gran, "daily")
          ? allDates[row]
          : format(new Date(allDates[row]), "dd-MM-yyyy'T'HH:mm:ss");

        sheet.cell(R.add(ri, 3), 1).string(dateVal);
        return curves.map((col: any, ci: number) => {
          const d = R.pipe<any, any, any>(
            R.filter(
              (x: any) =>
                R.equals(x.curveName, col) && R.equals(x.Date, allDates[row])
            ),
            R.pathOr(null, [0, "value"])
          )(arr);
          return R.isNil(d)
            ? sheet
            : sheet.cell(R.add(ri, 3), R.add(ci, 2)).number(d);
        });
      });
    }
  )(allDates);
};
